import { SpanStatusCode } from '@opentelemetry/api';
import { ZoneContextManager } from '@opentelemetry/context-zone';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { DocumentLoadInstrumentation } from '@opentelemetry/instrumentation-document-load';
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch';
import { XMLHttpRequestInstrumentation } from '@opentelemetry/instrumentation-xml-http-request';
import { Resource } from '@opentelemetry/resources';
import { BatchSpanProcessor, ReadableSpan } from '@opentelemetry/sdk-trace-base';
import { WebTracerProvider } from '@opentelemetry/sdk-trace-web';
import { ATTR_SERVICE_NAME } from '@opentelemetry/semantic-conventions';
import { traceRegistry } from './traceRegistry';

const TRACE_PROPAGATION_URLS = [/^https?:\/\/([a-z0-9-]+\.)*shapedigital\.com(?::\d+)?(\/|$)/i];

function getPathname(url: string): string {
  try {
    return new URL(url).pathname;
  } catch {
    return url;
  }
}

function normalizeUrl(url: string): string {
  const pathname = url.startsWith("/") ? url : getPathname(url);
  return pathname
    .replace(
      /\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi,
      "/:id",
    )
    .replace(/\/\d+/g, "/:id");
}

export const setupTelemetry = (serviceName: string) => {
  if (import.meta.env.VITE_OTEL_ENABLED !== 'true') return;

  const resource = new Resource({
    [ATTR_SERVICE_NAME]: serviceName,
  });

  const provider = new WebTracerProvider({
    resource,
    spanProcessors: [
      new BatchSpanProcessor(
        new OTLPTraceExporter({
          url: `${window.location.origin}/otlp/v1/traces`,
        }),
      ),
    ],
  });

  provider.register({
    contextManager: new ZoneContextManager(),
  });

  registerInstrumentations({
    instrumentations: [
      new DocumentLoadInstrumentation(),
      new FetchInstrumentation({
        ignoreUrls: [/localhost:8090\/sockjs-node/, /\.blob\.core\.windows\.net/],
        propagateTraceHeaderCorsUrls: TRACE_PROPAGATION_URLS,
        applyCustomAttributesOnSpan: (span, request, result) => {
          let url: string | undefined;
          if (request instanceof Request) url = request.url;
          if (!url) {
            const raw = (span as object as ReadableSpan).attributes['http.url'];
            if (typeof raw === 'string') url = raw;
          }
          span.setAttribute('http.route', url ? normalizeUrl(url) : 'unknown');
          span.setAttribute('http.instrumentation', 'fetch');
          if (result instanceof Response) {
            if (result.status >= 400) {
              span.setStatus({ code: SpanStatusCode.ERROR });
            }
          } else {
            span.setStatus({ code: SpanStatusCode.ERROR });
          }
        }
      }),
      new XMLHttpRequestInstrumentation({
        propagateTraceHeaderCorsUrls: TRACE_PROPAGATION_URLS,
        applyCustomAttributesOnSpan: (span, xhr) => {
          traceRegistry.set(xhr, span.spanContext().traceId);
          const url = xhr.responseURL || (span as object as ReadableSpan).attributes['http.url'] as string | undefined;
          span.setAttribute("http.route", url ? normalizeUrl(url) : "unknown");
          span.setAttribute('http.instrumentation', 'xhr');
          if (xhr.status >= 400) {
            span.setStatus({ code: SpanStatusCode.ERROR });
          }
        },
      }),
    ],
  });
};
